/**
 * Owner: Masa Bayu
 * File: commerce-payment.ts
 * Date:Time: 2026-09-15 22:46
 * Description: T3-E2 payment intent/attempt lifecycle with idempotent callbacks.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_PAYMENT_ATTEMPT_STATES = [
    'pending',
    'processing',
    'succeeded',
    'failed',
    'cancelled',
];
export const QXREAD_PAYMENT_CALLBACK_STATUSES = [
    'processing',
    'succeeded',
    'failed',
    'cancelled',
];
function assertAttemptState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_PAYMENT_ATTEMPT_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_PAYMENT_ATTEMPT_STATES.join(', ')}`);
    }
    return value;
}
function assertCallbackStatus(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_PAYMENT_CALLBACK_STATUSES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_PAYMENT_CALLBACK_STATUSES.join(', ')}`);
    }
    return value;
}
export function createPaymentIntent(order, options) {
    assertNoForbiddenFoundationKey(options, 'QXRead payment intent');
    if (order.state !== 'pending') {
        throw new Error('QXRead payment intent requires a pending order');
    }
    if (order.paymentAttemptId !== null) {
        throw new Error('QXRead payment intent blocked — order already linked to a payment attempt');
    }
    assertValidId(options.idempotencyKey, 'QXRead payment idempotencyKey');
    const intentId = options.intentId !== undefined ? options.intentId : createQxreadId('qxrdpayi');
    assertValidId(intentId, 'QXRead payment intentId');
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead payment intent createdAt');
    return Object.freeze({
        intentId,
        orderId: order.orderId,
        amountMinorUnits: order.totals.buyerTotalMinorUnits,
        currency: order.totals.currency,
        idempotencyKey: options.idempotencyKey,
        createdAt,
    });
}
export function createPaymentAttempt(intent, options) {
    assertNoForbiddenFoundationKey(options ?? {}, 'QXRead payment attempt');
    const attemptId = options?.attemptId !== undefined ? options.attemptId : createQxreadId('qxrdpaya');
    assertValidId(attemptId, 'QXRead payment attemptId');
    const idempotencyKey = options?.idempotencyKey ?? intent.idempotencyKey;
    assertValidId(idempotencyKey, 'QXRead payment attempt idempotencyKey');
    const createdAt = options?.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead payment attempt createdAt');
    return Object.freeze({
        attemptId,
        intentId: intent.intentId,
        orderId: intent.orderId,
        state: 'pending',
        idempotencyKey,
        providerReference: null,
        outcomeReason: null,
        createdAt,
        updatedAt: createdAt,
    });
}
export function markPaymentProcessing(attempt) {
    if (attempt.state !== 'pending' && attempt.state !== 'processing') {
        throw new Error(`QXRead payment cannot enter processing from "${attempt.state}"`);
    }
    const updatedAt = new Date().toISOString();
    return Object.freeze({
        ...attempt,
        state: 'processing',
        updatedAt,
    });
}
/**
 * Apply provider callback with duplicate protection via seen callback ids.
 * Terminal attempts are idempotent for the same outcome.
 */
export function applyPaymentCallback(attempt, callback, seenCallbackIds) {
    assertNoForbiddenFoundationKey(callback, 'QXRead payment callback');
    assertValidId(callback.callbackId, 'QXRead payment callbackId');
    assertValidId(callback.attemptId, 'QXRead payment callback attemptId');
    if (callback.attemptId !== attempt.attemptId) {
        throw new Error('QXRead payment callback attemptId mismatch');
    }
    const status = assertCallbackStatus(callback.status, 'QXRead payment callback status');
    const receivedAt = callback.receivedAt ?? new Date().toISOString();
    assertTimestamp(receivedAt, 'QXRead payment callback receivedAt');
    if (seenCallbackIds.has(callback.callbackId)) {
        return Object.freeze({
            attempt,
            duplicate: true,
            nextSeenCallbackIds: seenCallbackIds,
        });
    }
    const nextSeen = new Set(seenCallbackIds);
    nextSeen.add(callback.callbackId);
    if (attempt.state === 'succeeded' ||
        attempt.state === 'failed' ||
        attempt.state === 'cancelled') {
        if (attempt.state === status) {
            return Object.freeze({
                attempt,
                duplicate: true,
                nextSeenCallbackIds: Object.freeze(nextSeen),
            });
        }
        throw new Error(`QXRead payment attempt already terminal as "${attempt.state}" — cannot apply "${status}"`);
    }
    if (attempt.state === 'pending' && status !== 'processing' && status !== 'succeeded') {
        // allow failed/cancelled from pending (user abort) or processing path
    }
    const nextState = assertAttemptState(status, 'QXRead payment attempt state');
    return Object.freeze({
        attempt: Object.freeze({
            ...attempt,
            state: nextState,
            providerReference: callback.providerReference !== undefined
                ? callback.providerReference
                : attempt.providerReference,
            outcomeReason: callback.reason ?? attempt.outcomeReason,
            updatedAt: receivedAt,
        }),
        duplicate: false,
        nextSeenCallbackIds: Object.freeze(nextSeen),
    });
}
export function paymentAllowsEntitlement(attempt) {
    return attempt.state === 'succeeded';
}
export function paymentBlocksEntitlement(attempt) {
    return attempt.state !== 'succeeded';
}
//# sourceMappingURL=commerce-payment.js.map